<template>
  <div>
    <!-- Hero Section -->
    <section class="py-5 bg-white" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="10" xl="9" class="text-center">
            <h1 class="display-3 font-weight-bold marketing-heading mb-4" style="font-size: 3rem; line-height: 1.1;">
              Free Malaysian Business <span class="text-marketing-primary">Calculators</span>
            </h1>
            <p class="lead marketing-body mb-4" style="font-size: 1.25rem; max-width: 800px; margin-left: auto; margin-right: auto;">
              Accurate, compliant, and easy-to-use calculators for Malaysian businesses. No sign-up required. 100% free forever.
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Calculators Grid -->
    <section class="py-5 bg-marketing-light">
      <CContainer>
        <CRow>
          <!-- EPF Calculator -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-calculator" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3 text-dark">EPF Calculator</h5>
                <p class="marketing-body mb-4 text-dark">
                  Calculate employee and employer EPF contributions based on salary and age. Updated with latest 2026 rates.
                </p>
                <CButton class="btn-marketing-outline-primary" block to="/calculators/epf">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- SOCSO Calculator -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-calculator" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3 text-dark">SOCSO Calculator</h5>
                <p class="marketing-body mb-4 text-dark">
                  Calculate SOCSO contributions based on wage brackets. Includes SOCSO Table 1 and Table 2 calculations.
                </p>
                <CButton class="btn-marketing-outline-success" block to="/calculators/socso">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- SST Calculator -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-calculator" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3 text-dark">SST Calculator</h5>
                <p class="marketing-body mb-4 text-dark">
                  Calculate Sales & Service Tax (SST) for your invoices. Supports both 6% and 10% tax rates as well as inclusive/exclusive sums.
                </p>
                <CButton class="btn-marketing-outline-warning" block to="/calculators/sst">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Take-Home Salary Calculator -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-calculator" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3 text-dark">Take-Home Salary</h5>
                <p class="marketing-body mb-4 text-dark">
                  Calculate your net salary after EPF, SOCSO, EIS, and income tax deductions instantly without complicated formulas.
                </p>
                <CButton class="btn-marketing-outline-warning" block to="/calculators/salary">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

      </CContainer>
    </section>

    <!-- Why These Calculators Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center mb-5">
            <h2 class="h1 font-weight-bold marketing-heading mb-3">
              Why Use Our Calculators?
            </h2>
            <p class="lead marketing-body">
              Built by Malaysian business owners, for Malaysian business owners.
            </p>
          </CCol>
        </CRow>

        <CRow>
          <CCol lg="4" md="6" class="mb-4">
            <div class="text-center">
              <div class="marketing-icon-wrapper mx-auto mb-3">
                <CIcon name="cil-check-circle" size="xl"/>
              </div>
              <h5 class="font-weight-bold marketing-subheading mb-3">Always Accurate</h5>
              <p class="marketing-body">
                Updated with the latest Malaysian government rates and regulations. You can trust our calculations.
              </p>
            </div>
          </CCol>

          <CCol lg="4" md="6" class="mb-4">
            <div class="text-center">
              <div class="marketing-icon-wrapper mx-auto mb-3">
                <CIcon name="cil-speedometer" size="xl"/>
              </div>
              <h5 class="font-weight-bold marketing-subheading mb-3">Instant Results</h5>
              <p class="marketing-body">
                Get your calculations in seconds. No waiting, no complicated forms, no hassle.
              </p>
            </div>
          </CCol>

          <CCol lg="4" md="6" class="mb-4">
            <div class="text-center">
              <div class="marketing-icon-wrapper mx-auto mb-3">
                <CIcon name="cil-lock-locked" size="xl"/>
              </div>
              <h5 class="font-weight-bold marketing-subheading mb-3">100% Private</h5>
              <p class="marketing-body">
                Your data stays in your browser. We don't store or share any information you enter.
              </p>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- CTA Section -->
    <section class="py-5 bg-marketing-dark text-white" style="background: linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%);">
      <CContainer class="text-center">
        <h2 class="display-4 font-weight-bold mb-4" style="color: white;">
          Need More Than Calculators?
        </h2>
        <p class="lead mb-5" style="color: rgba(255, 255, 255, 0.9); max-width: 700px; margin-left: auto; margin-right: auto;">
          Automate your entire payroll, invoicing, and accounting with Niagaku. Save 14 hours monthly and eliminate manual errors.
        </p>
        <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center" style="gap: 1rem;">
          <CButton
            class="btn-marketing-accent mr-sm-3 mb-3 mb-sm-0 px-5 py-3 font-weight-600 btn-scale"
            size="lg"
            to="/signup"
          >
            Start Free 30-Day Trial
          </CButton>
          <CButton
            class="btn-outline-light-custom mr-sm-3 mb-3 mb-sm-0 px-5 py-3 btn-scale"
            size="lg"
            to="/features"
          >
            Explore Features
          </CButton>
        </div>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Calculators',
  metaInfo: {
    title: 'Free Malaysian Business Calculators - Niagaku',
    meta: [
      {
        name: 'description',
        content: 'Free EPF, SOCSO, EIS, and SST calculators for Malaysian businesses. Accurate calculations based on latest government rates. No sign-up required.'
      }
    ]
  }
}
</script>

<style scoped>
/* Calculators page uses marketing CSS classes */
</style>
